/**
 * Draft Privacy Policy + Messaging Terms for A2P review.
 * Not linked from parent consent until legal approves. Routes are public (no login).
 */
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Alert, Typography, Link } from '@mui/material';

function DraftBanner() {
  return (
    <Alert severity="warning" sx={{ mb: 3 }}>
      <strong>DRAFT — for legal review only.</strong> This text has not been approved for production or A2P
      registration. It is not linked from parent packet consent until legal signs off.
    </Alert>
  );
}

function PolicyShell({ title, children }) {
  return (
    <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 3 }, py: 4 }}>
      <DraftBanner />
      <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
        {title}
      </Typography>
      <Box sx={{ fontSize: '1rem', lineHeight: 1.7, color: 'text.primary', '& p': { mb: 2 } }}>
        {children}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 4 }}>
        See also:{' '}
        <Link component={RouterLink} to="/privacy">Privacy Policy</Link>
        {' · '}
        <Link component={RouterLink} to="/messaging-terms">Messaging Terms</Link>
      </Typography>
    </Box>
  );
}

export function PrivacyPolicyPage() {
  return (
    <PolicyShell title="Privacy Policy — Text Messaging & Automated Calls (Draft)">
      <Typography component="p">
        <strong>Hebrew Academy for Special Children, Inc.</strong> (HASC) collects the mobile phone number(s) you
        provide in order to send you text messages and place automated or artificial-intelligence (AI) voice calls
        related to your child&apos;s program and school operations.
      </Typography>
      <Typography component="p">
        <strong>
          We do not sell, rent, or share your phone number or opt-in information with third parties or affiliates
          for their own marketing purposes.
        </strong>{' '}
        Phone numbers and consent data are used only to deliver the communications you have agreed to, and are shared
        only with the service providers that operate our messaging and calling platform on our behalf (for example, our
        communications provider), under contract and solely to provide that service.
      </Typography>
      <Typography component="p">
        You may opt out of text messages at any time by replying STOP, and revoke consent to automated or AI voice calls
        by contacting the office at [HELP PHONE] or [HELP EMAIL]. Standard message and data rates may apply. For
        questions, reply HELP or contact [HELP PHONE / EMAIL].
      </Typography>
      <Typography component="p">
        [Link to / integrate with HASC&apos;s full Privacy Policy, HIPAA Notice of Privacy Practices, and FERPA notice
        once legal provides the canonical documents.]
      </Typography>
    </PolicyShell>
  );
}

export function MessagingTermsPage() {
  return (
    <PolicyShell title="SMS / Messaging Terms (Draft)">
      <Typography component="p">
        <strong>Program:</strong> HASC sends school-related text messages and automated or AI voice calls (program
        updates, schedule changes, closures, emergencies, transportation, attendance, health, reminders) to
        parents and guardians who opt in.
      </Typography>
      <Typography component="p">
        <strong>Frequency:</strong> Message frequency varies.
      </Typography>
      <Typography component="p">
        <strong>Cost:</strong> Message and data rates may apply.
      </Typography>
      <Typography component="p">
        <strong>Opt-out:</strong> Reply STOP to stop texts. Reply HELP for help. To stop automated or AI voice calls,
        contact [HELP PHONE / EMAIL].
      </Typography>
      <Typography component="p">
        <strong>Carriers:</strong> Carriers are not liable for delayed or undelivered messages.
      </Typography>
      <Typography component="p">
        <strong>Privacy:</strong> See our{' '}
        <Link component={RouterLink} to="/privacy">Privacy Policy</Link>.
      </Typography>
      <Typography component="p">
        <strong>Contact:</strong> Hebrew Academy for Special Children, Inc. — [PHONE / EMAIL / ADDRESS].
      </Typography>
    </PolicyShell>
  );
}
